import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Table, Button, Space, Modal, Form, Input, InputNumber, message, Spin, Select, Tag } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useConfig } from '../../contexts/ConfigContext'
import { useSecureRequest } from '../../hooks/useSecureRequest'
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus'

function PlansView() {
  const { config } = useConfig()
  const { securePost } = useSecureRequest()
  const [plans, setPlans] = useState<any[]>([])
  const [availableModules, setAvailableModules] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [editingId, setEditingId] = useState<number | null>(null)
  const didFetch = useRef(false)

  const fetchPlansAndModules = useCallback(async (isBackground = false) => {
    if (!config.key) return
    if (!isBackground) setLoading(true)
    try {
      const [plansRes, modulesRes] = await Promise.all([
        securePost('/plan/list', { session: config.session }),
        securePost('/module/list', { session: config.session })
      ])

      if (plansRes && Array.isArray(plansRes.plans)) {
        setPlans(plansRes.plans)
      } else {
        setPlans([])
      }

      if (modulesRes && Array.isArray(modulesRes.modules)) {
        setAvailableModules(modulesRes.modules)
      }
    } catch (err) {
      console.error(err)
      message.error('Error al cargar datos de planes')
    } finally {
      if (!isBackground) setLoading(false)
    }
  }, [config.key, config.session])

  useEffect(() => {
    if (config.key && !didFetch.current) {
      didFetch.current = true
      fetchPlansAndModules()
    }
  }, [config.key, fetchPlansAndModules])

  useRefetchOnFocus(fetchPlansAndModules)

  const handleOpenCreate = () => {
    setEditingId(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleOpenEdit = (record: any) => {
    setEditingId(record.id)
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      period: record.period,
      price: record.price,
      duration_days: record.duration_days,
      modules: record.modules || []
    })
    setModalVisible(true)
  }

  const handleSubmit = async (values: any) => {
    const { modules, ...planValues } = values
    try {
      if (editingId) {
        await securePost('/plan/update', {
          session: config.session,
          idPlan: editingId,
          plan: planValues,
          modules: modules || []
        })
        message.success('Plan actualizado exitosamente')
      } else {
        await securePost('/plan/create', {
          session: config.session,
          plan: planValues,
          modules: modules || []
        })
        message.success('Plan creado exitosamente')
      }
      setModalVisible(false)
      fetchPlansAndModules()
    } catch (err) {
      console.error(err)
      message.error('Error al guardar plan')
    }
  }

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '¿Está seguro de eliminar este plan?',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await securePost('/plan/toggle-status', {
            session: config.session,
            idPlan: id,
            is_active: false
          })
          message.success('Plan eliminado exitosamente')
          fetchPlansAndModules()
        } catch (err) {
          console.error(err)
          message.error('Error al eliminar plan')
        }
      }
    })
  }

  const moduleMap = React.useMemo(() => {
    const map = new Map<number, string>()
    availableModules.forEach(m => map.set(m.id, m.name))
    return map
  }, [availableModules])

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Nombre', dataIndex: 'name', key: 'name' },
    { title: 'Descripción', dataIndex: 'description', key: 'description' },
    { title: 'Período', dataIndex: 'period', key: 'period' },
    { 
      title: 'Precio', 
      dataIndex: 'price', 
      key: 'price',
      render: (price: number) => `$${price.toLocaleString()}`
    },
    { title: 'Duración (días)', dataIndex: 'duration_days', key: 'duration_days' },
    {
      title: 'Módulos Incluidos',
      dataIndex: 'modules',
      key: 'modules',
      render: (modIds: number[]) => (
        <Space wrap>
          {Array.isArray(modIds) && modIds.length > 0 ? (
            modIds.map(id => (
              <Tag color="blue" key={id}>
                {moduleMap.get(id) || `Módulo #${id}`}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#aaa', fontSize: '12px' }}>Ninguno</span>
          )}
        </Space>
      )
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space wrap size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(record)}>Editar</Button>
          <Button size="small" type="primary" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>Eliminar</Button>
        </Space>
      )
    }
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h3 style={{ margin: 0 }}>Gestión de Planes</h3>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Crear Plan
        </Button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '40px 0' }}>
          <Spin />
        </div>
      ) : (
        <Table dataSource={plans} columns={columns} rowKey="id" scroll={{ x: 'max-content' }} />
      )}

      <Modal
        title={editingId ? 'Editar Plan' : 'Crear Plan'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ period: 'monthly', duration_days: 30, modules: [] }}>
          <Form.Item name="name" label="Nombre del Plan" rules={[{ required: true, message: 'Ingrese nombre' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Descripción" rules={[{ required: true, message: 'Ingrese descripción' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="period" label="Período de Cobro" rules={[{ required: true, message: 'Seleccione período' }]}>
            <Input placeholder="Ej. monthly, yearly, bimensual" />
          </Form.Item>
          <Form.Item name="price" label="Precio" rules={[{ required: true, message: 'Ingrese precio' }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="duration_days" label="Duración en Días" rules={[{ required: true, message: 'Ingrese duración' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="modules" label="Módulos Incluidos en este Plan">
            <Select
              mode="multiple"
              placeholder="Seleccione los módulos que incluye el plan"
              options={availableModules.map(m => ({ label: m.name, value: m.id }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default PlansView
